import type { ExternalInflow, InflowType } from "../model/inp/inflows.ts";
import type { Pattern } from "../model/inp/patterns.ts";
import type { Pollutant } from "../model/inp/pollutants.ts";
import type { TimeSeries } from "../model/inp/timeseries.ts";
import { useT } from "../i18n/hooks.ts";
import { NumberInput } from "./fields.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

export function InflowsEditor({
  nodeName,
  pollutants,
  patterns,
  timeSeries,
  inflows,
  onChange,
}: {
  nodeName: string;
  pollutants: Pollutant[];
  patterns: Pattern[];
  timeSeries: TimeSeries[];
  inflows: ExternalInflow[];
  onChange: (rows: ExternalInflow[]) => void;
}) {
  const t = useT();
  const usedNames = new Set(inflows.map((i) => i.constituent));
  const availableToAdd = ["FLOW", ...pollutants.map((p) => p.name)].filter(
    (name) => !usedNames.has(name)
  );

  function addRow() {
    if (availableToAdd.length === 0) return;
    const constituent = availableToAdd[0];
    onChange([
      ...inflows,
      {
        node: nodeName,
        constituent,
        timeSeries: "",
        type: constituent === "FLOW" ? "FLOW" : "CONCEN",
        unitsFactor: 1,
        scaleFactor: 1,
        baseline: 0,
        basePattern: "",
      },
    ]);
  }

  function patchRow(constituent: string, patch: Partial<ExternalInflow>) {
    onChange(inflows.map((i) => (i.constituent === constituent ? { ...i, ...patch } : i)));
  }

  function removeRow(constituent: string) {
    onChange(inflows.filter((i) => i.constituent !== constituent));
  }

  /** "FLOW" is a UI label, translated; every other name is a real pollutant, shown exactly as the user named it. */
  function constituentLabel(name: string): string {
    return name === "FLOW" ? t("inflows.typeFlow") : name;
  }

  return (
    <section className="property-panel-group">
      <h3>{t("inflows.title")}</h3>
      {inflows.length === 0 && <p className="property-panel-hint">{t("inflows.noInflows")}</p>}
      {inflows.length > 0 && (
        <div className="named-collection-points-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{t("dwf.constituent")}</th>
                <th>{t("inflows.timeSeries")}</th>
                <th>{t("inflows.type")}</th>
                <th>{t("inflows.unitsFactor")}</th>
                <th>{t("inflows.scaleFactor")}</th>
                <th>{t("inflows.baseline")}</th>
                <th>{t("inflows.basePattern")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {inflows.map((inflow) => (
                <tr key={inflow.constituent}>
                  <td>
                    <select
                      value={inflow.constituent}
                      onChange={(e) =>
                        patchRow(inflow.constituent, { constituent: e.target.value })
                      }
                    >
                      <option value={inflow.constituent}>
                        {constituentLabel(inflow.constituent)}
                      </option>
                      {availableToAdd.map((name) => (
                        <option key={name} value={name}>
                          {constituentLabel(name)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={inflow.timeSeries}
                      onChange={(e) => patchRow(inflow.constituent, { timeSeries: e.target.value })}
                    >
                      <option value="" />
                      {timeSeries.map((ts) => (
                        <option key={ts.name} value={ts.name}>
                          {ts.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={inflow.type}
                      onChange={(e) =>
                        patchRow(inflow.constituent, { type: e.target.value as InflowType })
                      }
                    >
                      <option value="FLOW">{t("inflows.typeFlow")}</option>
                      <option value="CONCEN">{t("inflows.typeConcen")}</option>
                      <option value="MASS">{t("inflows.typeMass")}</option>
                    </select>
                  </td>
                  <td>
                    <NumberInput
                      value={inflow.unitsFactor}
                      onChange={(v) => patchRow(inflow.constituent, { unitsFactor: v })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={inflow.scaleFactor}
                      onChange={(v) => patchRow(inflow.constituent, { scaleFactor: v })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={inflow.baseline}
                      onChange={(v) => patchRow(inflow.constituent, { baseline: v })}
                    />
                  </td>
                  <td>
                    <select
                      value={inflow.basePattern}
                      onChange={(e) =>
                        patchRow(inflow.constituent, { basePattern: e.target.value })
                      }
                    >
                      <option value="" />
                      {patterns.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button-icon button-icon-danger"
                      onClick={() => removeRow(inflow.constituent)}
                      aria-label={t("inflows.deleteInflow")}
                      title={t("inflows.deleteInflow")}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button className="button" onClick={addRow} disabled={availableToAdd.length === 0}>
        {t("inflows.addInflow")}
      </button>
    </section>
  );
}
